import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useMemo, useState} from "react";
import {isAprilFool} from "../../utils/Utils";
import Footer from "../Footer/Footer";
import Icon from "../Icon/Icon.tsx";
import IconMap from "../Icon/IconMap.ts";
import SWCClock from "./SWCClock";
import {Link, NavLink as RouterNavLink, useLocation, useNavigate} from 'react-router';
import api, {useUser} from "../../utils/api";
import {AppShell, Button, Group, Burger, Stack, Switch, Divider, NavLink, Text, Box, ScrollArea, UnstyledButton} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import { PackageIcon, ClockIcon, CloudArrowUpIcon, ChartLineUpIcon, UserIcon, PowerIcon, CurrencyDollarIcon, MagnifyingGlassIcon, XIcon, StorefrontIcon, ShareNetworkIcon, UsersFourIcon, ListMagnifyingGlassIcon, GearIcon, UserGearIcon, UsersThreeIcon, NewspaperClippingIcon, FactoryIcon, SquaresFourIcon, CaretRightIcon } from "@phosphor-icons/react";
import SurveyBanner from "../SurveyBanner/SurveyBanner";

function getLogoAdditionalStyles() {
    if (isAprilFool) {
        if (Math.random() < 0.01) {
            return {transform: 'rotate(180deg)'};
        }

        const MIN_VALUE = 4;
        const MAX_VALUE = 10;
        let rotation = Math.floor(Math.pow(Math.random(), 2) * (MAX_VALUE - MIN_VALUE + 1)) + MIN_VALUE;
        rotation = Math.random() < 0.5 ? rotation : -rotation;

        return {transform: `rotate(${rotation}deg)`};
    }

    return {};
}

function ForgeIcon({name, size = 16}) {
    return <Icon icon={IconMap[name]} style={{width: size, height: size}}/>;
}

function pathMatches(pathname, matches = [], exact = false) {
    return matches.some((match) => {
        if (exact) {
            return pathname === match;
        }

        return pathname === match || pathname.startsWith(`${match}/`);
    });
}

const COLLAPSED_SECTIONS_KEY = 'forge.nav.collapsedSections';

function sectionIsActive(section, pathname) {
    return section.links.some((link) => pathMatches(pathname, link.matches, link.exact));
}

function readCollapsedSections() {
    try {
        const raw = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function writeCollapsedSections(labels) {
    try {
        localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(labels));
    } catch {
        /* ignore unavailable storage */
    }
}

const NavBar = ({children, showSurveyBanner = false}) => {
    const {user} = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [mobileOpen, {toggle: toggleMobile, close: closeMobile}] = useDisclosure(false);
    const [collapsedSections, setCollapsedSections] = useState(readCollapsedSections);

    useEffect(() => {
        closeMobile();
    }, [location.pathname, closeMobile]);

    const toggleSection = useCallback((label) => {
        setCollapsedSections((prev) => {
            const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
            writeCollapsedSections(next);
            return next;
        });
    }, []);

    const toggleUserSub = useCallback((value) => {
        if (import.meta.env.DEV) {
            queryClient.setQueryData(['user'], (oldData) => {
                if (!oldData) {
                    return oldData;
                }

                return {
                    ...oldData,
                    user: {...oldData.user, subLevel: value}
                };
            });
        } else {
            console.error('Tried using a dev feature!');
        }
    }, [queryClient]);

    const logoAdditionalStyle = getLogoAdditionalStyles();

    const logout = useCallback(() => {
        api.get('/logout').then(() => {
            queryClient.invalidateQueries({queryKey: ['user']});
            navigate('/');
        });
    }, [queryClient, navigate]);

    const onNavClick = useCallback(() => {
        closeMobile();
    }, [closeMobile]);

    const onMobileLogout = useCallback(() => {
        closeMobile();
        logout();
    }, [closeMobile, logout]);

    const navSections = useMemo(() => {
        const sections = [
            {
                label: 'Bounty Hunting',
                icon: <ForgeIcon name="BountyHunting"/>,
                links: [
                    {label: 'Contract Tracking', to: '/bh/contracts', icon: <ForgeIcon name="Target"/>, matches: ['/bh/contracts', '/bh/contract']},
                    {label: 'Target Heatmap', to: '/bh/heatmap', icon: <MagnifyingGlassIcon />, matches: ['/bh/heatmap']},
                    {label: 'Report Sighting', to: '/bh/reportSightings', icon: <ForgeIcon name="HerdDaily"/>, matches: ['/bh/reportSightings']},
                    {label: 'Taverns', to: '/bh/taverns', icon: <ShareNetworkIcon />, matches: ['/bh/taverns']},
                    ...(user.privs?.manageTavernNetwork || user.isAdmin
                        ? [{label: 'Manage Tavern Network', to: '/bh/network/manage', icon: <ForgeIcon name="Facility"/>, matches: ['/bh/network/manage']}]
                        : []),
                ]
            },
            {
                label: 'Galaxy',
                icon: <ForgeIcon name="Galaxy"/>,
                links: [
                    {label: 'Hyper Routes', to: '/hl/routes', icon: <ForgeIcon name="HyperRoutes"/>, matches: ['/hl/routes']},
                    {label: 'Galaxy Map', to: '/map', icon: <ForgeIcon name="System"/>, matches: ['/map'], disabled: !(user.subLevel > 0)},
                    {label: 'Asteroid Fields', to: '/asteroid-fields', icon: <ForgeIcon name="AsteroidMining"/>, matches: ['/asteroid-fields']},
                ]
            },
            {
                label: 'Cargo',
                icon: <PackageIcon size={16}/>,
                links: [
                    {label: 'Calculator', to: '/cargo/cargo', icon: <PackageIcon size={16}/>, matches: ['/cargo/cargo', '/cargo/calculator']},
                    {label: 'Fitouts', to: '/cargo/fitout', icon: <ForgeIcon name="Ship"/>, matches: ['/cargo/fitout']},
                ]
            },
            {
                label: 'Vendors',
                icon: <CurrencyDollarIcon size={16}/>,
                links: [
                    {label: 'Vendors', to: '/vendors', icon: <StorefrontIcon />, matches: ['/vendors']},
                    {label: 'Wares', to: '/wares', icon: <ForgeIcon name="Datacard"/>, matches: ['/wares']},
                ]
            },
            {
                label: 'Modules',
                icon: <SquaresFourIcon />,
                links: [
                    {label: 'Production', to: '/modules/production', icon: <FactoryIcon />, matches: ['/modules/production'], moduleType: 'production'},
                ]
            },
            {
                label: 'Other Tools',
                icon: <ForgeIcon name="Cross"/>,
                links: [
                    {label: 'Discord Timestamps', to: '/misc/timestamp', icon: <ClockIcon size={16}/>, matches: ['/misc/timestamp']},
                    {label: 'Vision Simulator', to: '/vision', icon: <ForgeIcon name="Vision"/>, matches: ['/vision'], disabled: !(user.subLevel > 0)},
                    {label: 'Image Hosting', to: '/files', icon: <CloudArrowUpIcon size={16}/>, matches: ['/files'], disabled: !(user.subLevel > 0)},
                    {label: 'Browser Extension', to: '/misc/browser-extension', icon: <ForgeIcon name="SwcRules"/>, matches: ['/misc/browser-extension']},
                    {label: 'Charts', to: '/charts', icon: <ChartLineUpIcon size={16}/>, matches: ['/charts']},
                    {label: 'GNS Archive', to: '/gns/archive/post', icon: <NewspaperClippingIcon />, matches: ['/gns/archive']},
                ]
            },
            {
                label: 'Account',
                icon: <UserIcon size={16}/>,
                links: [
                    {label: 'API Permissions', to: '/user/api-permissions', icon: <UserIcon size={16}/>, matches: ['/user/api-permissions']},
                    ...(user.privs.managePermissions
                        ? [{label: 'Manage Faction', to: '/faction', icon: <UsersThreeIcon />, matches: ['/faction'], exact: true}]
                        : []),
                    {label: 'Subscribe', to: '/subscribe', icon: <ForgeIcon name="Credits"/>, matches: ['/subscribe'], exact: true},
                    {label: 'Manage Subscription', to: '/subscribe/manage', icon: <ForgeIcon name="Credits"/>, matches: ['/subscribe/manage']},
                    ...(user.privs.manageLicenses
                        ? [{label: 'Manage Faction Licenses', to: '/faction/licenses', icon: <UserGearIcon />, matches: ['/faction/licenses']}]
                        : []),
                ]
            },
        ];

        if (user.isAdmin) {
            sections.splice(sections.length - 1, 0, {
                label: 'Admin',
                icon: <GearIcon />,
                links: [
                    {label: 'Users', to: '/admin/users', icon: <UserIcon size={16}/>, matches: ['/admin/users']},
                    {label: 'Factions', to: '/admin/factions', icon: <UsersFourIcon />, matches: ['/admin/factions']},
                    {label: 'Subscriptions', to: '/admin/subscriptions', icon: <ForgeIcon name="Credits"/>, matches: ['/admin/subscriptions']},
                    {label: 'Modules', to: '/admin/modules', icon: <ForgeIcon name="Datacard"/>, matches: ['/admin/modules']},
                    {label: 'Logs', to: '/admin/logs', icon: <ListMagnifyingGlassIcon />, matches: ['/admin/logs']},
                    {label: 'File Hosting', to: '/admin/files', icon: <CloudArrowUpIcon size={16}/>, matches: ['/admin/files']},
                ]
            });
        }

        // Drop module-gated links the user has no instance access to, then hide any
        // section left without links (e.g. the Modules group when no modules are accessible).
        const moduleTypes = user.moduleTypes ?? [];
        return sections
            .map((section) => ({
                ...section,
                links: section.links.filter((link) => !link.moduleType || moduleTypes.includes(link.moduleType)),
            }))
            .filter((section) => section.links.length > 0);
    }, [user]);

    useEffect(() => {
        const activeSection = navSections.find((section) => sectionIsActive(section, location.pathname));
        if (!activeSection) {
            return;
        }

        setCollapsedSections((prev) => {
            if (!prev.includes(activeSection.label)) {
                return prev;
            }

            const next = prev.filter((l) => l !== activeSection.label);
            writeCollapsedSections(next);
            return next;
        });
    }, [location.pathname, navSections]);

    return (
        <AppShell
            layout="alt"
            header={{height: 58}}
            navbar={{
                width: {base: 280, md: 280},
                breakpoint: 'md',
                collapsed: {mobile: !mobileOpen},
            }}
            footer={{height: 46}}
            padding="md"
        >
            <AppShell.Header
                style={{
                    backgroundColor: 'var(--mantine-color-dark-7)',
                    borderBottom: '1px solid var(--mantine-color-dark-4)',
                }}
            >
                <Group h="100%" px="md" justify="space-between" wrap="nowrap">
                    <Link to="/"><img src="/logo-100.png" alt="" className="logo" style={logoAdditionalStyle}/></Link>
                    <Group gap="sm" wrap="nowrap">
                        {import.meta.env.DEV ? (
                            <>
                                <Switch
                                    checked={user.subLevel > 0}
                                    onChange={e => toggleUserSub(e.currentTarget.checked ? 1 : 0)}
                                    size="sm"
                                />
                                <Text size="sm" fw={700} c="forge.5" visibleFrom="sm">DEV</Text>
                            </>
                        ) : null}
                        <SWCClock/>
                        <Text size="sm" fw={600} visibleFrom="sm">{user.handle}</Text>
                        <Button
                            leftSection={<PowerIcon size={16}/>}
                            size="compact-sm"
                            variant="subtle"
                            color="gray"
                            onClick={logout}
                            visibleFrom="md"
                        >
                            Logout
                        </Button>
                    </Group>
                    <Burger opened={mobileOpen} onClick={toggleMobile} hiddenFrom="md" size="sm"/>
                </Group>
            </AppShell.Header>

            <AppShell.Navbar
                p="sm"
                style={{
                    backgroundColor: 'var(--mantine-color-dark-7)',
                    borderRight: '1px solid var(--mantine-color-dark-4)',
                }}
            >
                <ScrollArea h="100%" type="auto" scrollbarSize={6}>
                    <Stack gap="sm" h="100%">
                        <Group justify="space-between" hiddenFrom="md" px="xs">
                            <Text fw={700} size="sm" c="dimmed">Navigation</Text>
                            <Button
                                size="compact-sm"
                                variant="subtle"
                                color="gray"
                                leftSection={<XIcon size={14}/>}
                                onClick={closeMobile}
                            >
                                Close
                            </Button>
                        </Group>
                        <Divider hiddenFrom="md"/>
                        <Stack gap={4} style={{flex: 1}}>
                            {navSections.map((section) => {
                                const opened = !collapsedSections.includes(section.label);

                                return (
                                    <Box key={section.label}>
                                        <UnstyledButton
                                            className="forge-nav-section-header"
                                            onClick={() => toggleSection(section.label)}
                                            aria-expanded={opened}
                                        >
                                            <Group gap="xs" px="xs" py={6} wrap="nowrap" justify="space-between">
                                                <Group gap="xs" wrap="nowrap">
                                                    {section.icon}
                                                    <Text fw={700} size="xs" c="forge.7" tt="uppercase">{section.label}</Text>
                                                </Group>
                                                <CaretRightIcon
                                                    size={12}
                                                    style={{
                                                        color: 'var(--mantine-color-dark-2)',
                                                        transition: 'transform 150ms ease',
                                                        transform: opened ? 'rotate(90deg)' : 'none',
                                                    }}
                                                />
                                            </Group>
                                        </UnstyledButton>
                                        {opened ? (
                                            <Stack gap={2} mt={2} pl="md">
                                                {section.links.map((link) => (
                                                    <NavLink
                                                        key={link.label}
                                                        label={link.label}
                                                        leftSection={link.icon}
                                                        className="forge-side-nav-link"
                                                        component={RouterNavLink}
                                                        to={link.to}
                                                        onClick={onNavClick}
                                                        disabled={link.disabled}
                                                        active={pathMatches(location.pathname, link.matches, link.exact)}
                                                        variant="filled"
                                                        color="dimmed"
                                                    />
                                                ))}
                                            </Stack>
                                        ) : null}
                                    </Box>
                                );
                            })}
                        </Stack>
                        <Box hiddenFrom="md">
                            <Divider my="sm"/>
                            <NavLink
                                label="Logout"
                                leftSection={<PowerIcon size={16}/>}
                                className="forge-side-nav-link"
                                onClick={onMobileLogout}
                                variant="light"
                            />
                        </Box>
                    </Stack>
                </ScrollArea>
            </AppShell.Navbar>

            <AppShell.Main>
                {showSurveyBanner ? <SurveyBanner/> : null}
                {children}
            </AppShell.Main>
            <AppShell.Footer>
                <Footer />
            </AppShell.Footer>
        </AppShell>
    );
};

export default NavBar;
